'use client';

import { useEffect, useRef } from 'react';
import { LockKeyhole } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';

interface NotificationModalProps {
  closeModal: () => void;
}
export function InAppNotificationBanner({ closeModal }: NotificationModalProps) {
  const previouslyFocusedElement = useRef<HTMLElement | null>(
    typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );

  useEffect(
    () => () => {
      if (previouslyFocusedElement.current?.isConnected) {
        previouslyFocusedElement.current.focus();
      }
    },
    [],
  );
  return (
    <Dialog open onOpenChange={(open) => !open && closeModal()}>
      <DialogContent
        data-kor-in-app-browser-prompt=""
        showCloseButton={false}
        onCloseAutoFocus={() => undefined}
        overlayClassName="!z-[9999] !bg-black/10 backdrop-blur-[1px]"
        className="!z-[10000] !grid !gap-0 origin-top-right overflow-hidden !border-white/80 !bg-transparent !p-4 text-[#170e08] backdrop-blur-[26px] backdrop-saturate-[1.65] data-[state=closed]:!slide-out-to-right-2 data-[state=closed]:!slide-out-to-top-2 data-[state=open]:!slide-in-from-right-2 data-[state=open]:!slide-in-from-top-2 sm:!rounded-[26px]"
        style={{
          left: 'auto',
          right: '14px',
          top: 'calc(env(safe-area-inset-top, 0px) + 14px)',
          fontFamily:
            'Nunito, ui-rounded, "SF Pro Rounded", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          width: 'min(326px, calc(100vw - 28px))',
          maxWidth: 'none',
          transform: 'none',
          borderRadius: '26px',
          background:
            'linear-gradient(145deg, rgba(255,255,255,.86), rgba(255,255,255,.62)), linear-gradient(35deg, rgba(255,96,13,.07), transparent 52%)',
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,.95), inset 0 -1px 0 rgba(255,255,255,.28), 0 20px 50px rgba(33,24,18,.22), 0 4px 14px rgba(33,24,18,.12)',
        }}>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-[inherit]"
          style={{
            background: 'radial-gradient(120% 55% at 8% 0%, rgba(255,255,255,.7), transparent 52%)',
          }}
        />
        <style>{`
          @media (prefers-reduced-transparency: reduce) {
            [data-kor-in-app-browser-prompt] {
              background: #f8f4ef !important;
              backdrop-filter: none !important;
              -webkit-backdrop-filter: none !important;
            }
          }
        `}</style>

        <div className="relative flex items-center gap-2.5">
          <div
            aria-hidden="true"
            className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-xl border border-white/70 text-[#8f3300]"
            style={{
              background: 'linear-gradient(145deg, rgba(255,154,92,.7), rgba(255,96,13,.3))',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,.75), 0 6px 14px rgba(255,96,13,.18)',
            }}>
            <LockKeyhole className="h-4 w-4" strokeWidth={2.2} />
          </div>
          <DialogTitle className="text-base font-bold leading-tight tracking-[-0.01em] text-[#170e08]">
            For a secure, full experience:
          </DialogTitle>
        </div>

        <DialogDescription asChild>
          <ol className="relative my-3.5 grid list-none gap-2.5 text-[13px] leading-[1.42] text-[#2d3139]">
            <li className="grid grid-cols-[24px_1fr] items-start gap-2.5">
              <span
                aria-hidden="true"
                className="grid h-6 w-6 place-items-center rounded-[9px] border border-white/75 bg-white/55 text-xs font-black text-[#663016] shadow-[inset_0_1px_0_rgba(255,255,255,.72)]">
                1
              </span>
              <span className="pt-0.5">Tap the ... or ⋮ icon in the top right.</span>
            </li>
            <li className="grid grid-cols-[24px_1fr] items-start gap-2.5">
              <span
                aria-hidden="true"
                className="grid h-6 w-6 place-items-center rounded-[9px] border border-white/75 bg-white/55 text-xs font-black text-[#663016] shadow-[inset_0_1px_0_rgba(255,255,255,.72)]">
                2
              </span>
              <span className="pt-0.5">
                Select <strong>Open in Safari</strong> or <strong>Open in browser.</strong>
              </span>
            </li>
          </ol>
        </DialogDescription>

        <button
          type="button"
          onClick={closeModal}
          className="relative min-h-11 w-full rounded-full border border-white/50 text-sm font-black tracking-[0.01em] text-[#291205] transition-transform hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff600d] focus-visible:ring-offset-2 active:scale-[0.99]"
          style={{
            background: 'linear-gradient(180deg, rgba(255,132,67,.95), rgba(255,96,13,.88))',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,.55), 0 8px 20px rgba(255,96,13,.23)',
          }}>
          OK
        </button>
      </DialogContent>
    </Dialog>
  );
}
